import * as pdfMake from "pdfmake/build/pdfmake.js";

import { ReservationsGetModel } from "@/commons/services/Reservations";
import DateUtil from "@/plugins/date-util/DateUtil";
import { ReservationSettingsGetModel } from "../services/ReservationSettings";
import { OnlineReservationSettingsGetModel } from "../services/OnlineReservationSettings";
import { FacilityGetModel } from "../services/Facilities";

/**
 * ### 面会予約リマインドのPDF操作
 */
export class RemindReservationsPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /** 施設情報 */
  facility: FacilityGetModel;
  /** 訪問面会設定 */
  visitSetting: ReservationSettingsGetModel;
  /** オンライン面会設定 */
  onlineSetting: OnlineReservationSettingsGetModel;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string, facility: FacilityGetModel, visitSetting: ReservationSettingsGetModel, onlineSetting: OnlineReservationSettingsGetModel) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;
    this.facility = facility;
    this.visitSetting = visitSetting;
    this.onlineSetting = onlineSetting;

    pdfMake.vfs = { "RemindReservationsPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      RemindReservationsPdf: {
        normal: "RemindReservationsPdf.ttf",
        bold: "RemindReservationsPdf.ttf"
      }
    };

    return;
  }

  /**
   * ### 面会予約リマインドPDFを作成してダウンロードさせる
   *
   * @param items 印刷対象の面会予約リマインド
   * @param facilityName 施設名
   * @param fileName ファイル名
   */
  public createAndDownloadPdf(items: Array<ReservationsGetModel>, facilityName: string, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [15, 14, 15, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 10,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "RemindReservationsPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 面会予約リマインド一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   *   - 作成日
   *   - タイトル
   *   - 施設名
   * - コンテンツ部(面会予約リマインド情報)
   */
  private createContent(items: Array<ReservationsGetModel>, facilityName: string): any {
    let contents: Array<any> = [];
    contents = this.createHeader(facilityName);
    contents.push(this.createTable(items));
    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   *
   * @param facilityName 施設名
   */
  private createHeader(facilityName: string): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: "HitomeQコネクト 本日の面会予約一覧",
      style: "title"
    });

    // 施設名、利用者名
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 面会予約リマインド情報
   */
  private createTable(items: Array<ReservationsGetModel>): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        // 各列の幅指定、列数が動的なのであとで格納する
        widths: []
      }
    };
    // 動的に列数が変わるので列に関わるものは個別に追加していく
    const width: number[] = [];
    const rows: Array<any> = [];

    // 種別・状態
    width.push(50);
    rows.push({ text: "種別\n状態", style: "tableHeader" });
    // width.push(50);
    // rows.push({ text: "状態", style: "tableHeader" });
    // 面会日時
    width.push(76);
    rows.push({ text: "面会日時", style: "tableHeader" });

    // 「訪問面会を使用している」かつ「訪問面会の面会場所」を使用している場合は、面会場所を追加
    if (this.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useLocation == true) {
      width.push(60);
      rows.push({ text: "面会場所", style: "tableHeader" });
    }
    // 利用者名（部屋名）
    width.push(65);
    rows.push({ text: "利用者名\n（部屋名）", style: "tableHeader" });
    // 面会者名（続柄）
    width.push(65);
    rows.push({ text: "面会者名\n（続柄）", style: "tableHeader" });
    // 電話番号
    width.push(64);
    rows.push({ text: "電話番号", style: "tableHeader" });

    // 「訪問面会」と「オンライン面会」を使用していて、「家族からの連絡事項」を「使用している」ものがあれば、家族からの連絡事項を追加
    if (
      (this.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useText == true) ||
      (this.facility.permittedFunctions.onlineReservation == true && this.onlineSetting && this.onlineSetting.useText == true)
    ) {
      width.push(124);
      rows.push({ text: "家族からの連絡事項", style: "tableHeader" });
    }

    // 列幅、列情報を格納する
    tableContent.table.widths = width;
    const body: Array<any> = [rows];

    for (const item of items) {
      const typeName: string = item.type == 0 ? "訪問面会" : "オンライン面会";
      const status: string = item.deleteFlag == true ? "キャンセル済" : item.consented == false ? "未承諾" : "承諾済";
      const datetime: string = this.getReservationDatetime(Number(item.datetime));

      const line: Array<any> = [];
      // 種別・状態
      line.push({ text: `${typeName}\n${status}`, style: { alignment: "center" } });
      // line.push({ text: status, style: { alignment: "center" } });
      // 面会日時
      line.push({ text: datetime, style: { alignment: "center" } });
      // 「訪問面会を使用している」かつ「訪問面会の面会場所」を使用している場合は、面会場所を追加
      if (this.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useLocation == true) {
        line.push(item.location ? item.location : "");
      }
      // 利用者名（部屋名）
      line.push(`${item.residentName}\n（${item.residentRoom}）`);
      // 面会者名（続柄）
      line.push(`${item.familyName}\n（${item.familyRelation}）`);
      // 電話番号
      line.push(item.familyTel);
      // 「訪問面会」と「オンライン面会」を使用していて、「家族からの連絡事項」を「使用している」ものがあれば、家族からの連絡事項を追加
      if (
        (this.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useText == true) ||
        (this.facility.permittedFunctions.onlineReservation == true && this.onlineSetting && this.onlineSetting.useText == true)
      ) {
        line.push({ text: item.text ? item.text : "", style: "" });
      }

      body.push(line);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  private getReservationDatetime(datetime: number): string {
    const dateObject: Date = this.dateUtil.convertNumberToDate(datetime);
    const date: string = this.dateUtil.convertDateToString(dateObject, "mm月dd日");
    const week: string = this.dateUtil.getWeekName(dateObject, "ja");
    const time: string = this.dateUtil.convertDateToString(dateObject, "hh:MM");
    return `${date}（${week}）${time}`;
  }
}
